import React, { useEffect, useState } from "react";
import EmailVerify from "./Components/Dashbord/EmailVerify";
import { oneKlicks } from "../../SDK/oneclick";
import {
    Card,
    Button,
    Select,
    Input,
    InputNumber,
    Space,
    Typography,
    Divider,
    Tag,
    Tooltip,
    Empty,
    Form,
    Popconfirm,
    Badge,
    Skeleton
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    MedicineBoxOutlined,
    DollarCircleOutlined,
    InfoCircleOutlined,
    CheckCircleFilled,
    SaveOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { getuserdata } from "../../services/apiService";

const { Title, Text } = Typography;
const { Option } = Select;

const SpecialitiesServices = () => {
    const [userdata, setUserdata] = useState({});
    const [specialities, setSpecialities] = useState([]);
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [specialityOptions, setSpecialityOptions] = useState([]);
    const [doctorSpecialities, setDoctorSpecialities] = useState([]);
    const [localDoctorId, setLocalDoctorId] = useState("");

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const userDataStr = localStorage.getItem("userData");
            const docId = localStorage.getItem("doctorId");
            setLocalDoctorId(docId);
            if (userDataStr) setUserdata(JSON.parse(userDataStr));

            await fetchSpecialities();
            await fetchServices();
            await fetchDoctorSpecialities();
            setLoading(false);
        };
        init();
    }, []);

    const fetchDoctorSpecialities = async () => {
        try {
            const dId = localDoctorId ? String(localDoctorId) : String(await getuserdata('id'));
            const response = await oneKlicks.read("", "doctor_specialities", { doctor_id: dId }, "id DESC", "", "", "");
            if (response.success && response.data) {
                setDoctorSpecialities(response.data);

                // Group by speciality_id
                const grouped = {};
                response.data.forEach(item => {
                    const specId = String(item.speciality_id);
                    if (!grouped[specId]) {
                        grouped[specId] = {
                            id: `spec_${specId}`,
                            speciality: specId,
                            services: []
                        };
                    }
                    grouped[specId].services.push({
                        id: `srv_${item.id}`,
                        service: item.service_id,
                        price: item.price,
                        description: item.description,
                        old_service_id: item.id,
                        name: item.name
                    });
                });
                setSpecialities(Object.values(grouped));
            }
        } catch (error) {
            toast.error("Failed to load your specialities");
        }
    };

    const fetchSpecialities = async () => {
        try {
            const response = await oneKlicks.read("", "specialities", {}, "id ASC", "", "", "");
            if (response.success) setSpecialityOptions(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchServices = async () => {
        try {
            const response = await oneKlicks.read("", "services", {}, "id ASC", "", "", "");
            if (response.success) setServices(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleAddSpeciality = () => {
        const allSpecIds = specialities.map(s => String(s.speciality));
        const available = specialityOptions.find(opt => !allSpecIds.includes(String(opt.id)));
        if (!available) {
            toast.info('All available specialities are already added');
            return;
        }
        const newId = `temp_${Date.now()}`;
        setSpecialities([{ id: newId, speciality: String(available.id), services: [] }, ...specialities]);
    };

    const handleAddService = (specId) => {
        setSpecialities(specialities.map(spec => {
            if (spec.id === specId) {
                const existingSrvIds = (spec.services || []).map(s => String(s.service));
                const available = services.find(srv =>
                    String(srv.speciality_id) === String(spec.speciality) &&
                    !existingSrvIds.includes(String(srv.id))
                );

                const newSrv = {
                    id: `temp_srv_${Date.now()}`,
                    service: available ? available.id : null,
                    price: "",
                    description: ""
                };

                return { ...spec, services: [newSrv, ...(spec.services || [])] };
            }
            return spec;
        }));
    };

    const handleRemoveSpeciality = async (specId) => {
        const specToDelete = specialities.find(spec => spec.id === specId);
        if (specToDelete) {
            // Find all services under this specialty that have old_service_id (already saved in DB)
            const dbServices = (specToDelete.services || []).filter(s => s.old_service_id);
            if (dbServices.length > 0) {
                try {
                    const deletePromises = dbServices.map(s =>
                        oneKlicks.delete("", "doctor_specialities", { id: s.old_service_id })
                    );
                    await Promise.all(deletePromises);
                    toast.success("Speciality and its services removed successfully");
                } catch (err) {
                    toast.error("Failed to delete speciality services from database");
                    return;
                }
            }
        }
        setSpecialities(specialities.filter(spec => spec.id !== specId));
    };

    const handleRemoveService = async (specId, srvId) => {
        setSpecialities(specialities.map(spec => {
            if (spec.id === specId) {
                const srvToDelete = spec.services.find(s => s.id === srvId);
                if (srvToDelete?.old_service_id) {
                    oneKlicks.delete("", "doctor_specialities", { id: srvToDelete.old_service_id })
                        .then(() => toast.success("Service removed from profile"))
                        .catch(() => toast.error("Failed to remove service"));
                }
                return { ...spec, services: spec.services.filter(s => s.id !== srvId) };
            }
            return spec;
        }));
    };

    const handleDataChange = (specId, srvId, field, value) => {
        setSpecialities(specialities.map(spec => {
            if (spec.id === specId) {
                return {
                    ...spec,
                    services: spec.services.map(srv =>
                        srv.id === srvId ? { ...srv, [field]: value } : srv
                    )
                };
            }
            return spec;
        }));
    };

    const handleSpecialityChange = (specId, val) => {
        setSpecialities(specialities.map(spec =>
            spec.id === specId ? { ...spec, speciality: val, services: [] } : spec
        ));
    };

    const handleUpdateAll = async () => {
        setSaving(true);
        try {
            const dId = await getuserdata('id');
            const allSrvs = specialities.flatMap(spec =>
                (spec.services || []).map(srv => ({ ...srv, specId: spec.speciality }))
            );

            for (const srv of allSrvs) {
                if (!srv.service) continue;

                const data = {
                    speciality_id: String(srv.specId),
                    service_id: String(srv.service),
                    price: srv.price !== undefined && srv.price !== null ? String(srv.price) : '',
                    description: srv.description || '',
                    doctor_id: String(dId),
                    status: 'active'
                };

                if (srv.old_service_id) {
                    await oneKlicks.update("", "doctor_specialities", data, { id: srv.old_service_id });
                } else {
                    await oneKlicks.create("", "doctor_specialities", data, {});
                }
            }
            toast.success("Profile services updated successfully");
            await fetchDoctorSpecialities();
        } catch (error) {
            toast.error("Failed to update services");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="specialities-services-modern" style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh' }}>
            {userdata.activation_key == null && <EmailVerify />}

            <style>{`
                .premium-spec-card {
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 24px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    overflow: hidden;
                    transition: all 0.3s ease;
                }
                .premium-spec-card:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }
                .spec-header {
                    background: #fff;
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .service-row {
                    background: #fcfdfe;
                    padding: 16px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    transition: background 0.2s;
                }
                .service-row:hover {
                    background: #f8fafc;
                }
                .service-row:last-child {
                    border-bottom: none;
                }
                .ant-input-number, .ant-input, .ant-select-selector {
                    border-radius: 8px !important;
                }
            `}</style>

            <div className="container-fluid">
                <div className="justify-content-between align-items-center mb-5">
                    <div>
                        <Title level={2} style={{ margin: 0, fontWeight: 800 }}>My Specialities & Services</Title>
                        <Text type="secondary">Manage the medical services and pricing you offer to patients</Text>
                    </div>
                    <Space>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={handleAddSpeciality}
                            style={{ borderRadius: '12px', height: '48px', fontWeight: 600, background: '#2563eb', borderColor: '#2563eb' }}
                        >
                            Add Speciality
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            size="large"
                            loading={saving}
                            onClick={handleUpdateAll}
                            style={{ borderRadius: '12px', height: '48px', fontWeight: 600, background: '#10b981', borderColor: '#10b981' }}
                        >
                            Update All
                        </Button>
                    </Space>
                </div>

                {loading ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                ) : specialities.length > 0 ? (
                    specialities.map((spec) => (
                        <div key={spec.id} className="premium-spec-card">
                            <div className="spec-header">
                                <Space size="middle" style={{ flex: 1 }}>
                                    <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '12px' }}>
                                        <MedicineBoxOutlined style={{ fontSize: '20px', color: '#2563eb' }} />
                                    </div>
                                    <Select
                                        value={String(spec.speciality)}
                                        onChange={(v) => handleSpecialityChange(spec.id, v)}
                                        style={{ minWidth: '250px' }}
                                        size="large"
                                        placeholder="Select Speciality"
                                        showSearch
                                    >
                                        {specialityOptions.map(opt => (
                                            <Option key={opt.id} value={String(opt.id)}>
                                                {opt.name.replace(/&amp;/g, '&')}
                                            </Option>
                                        ))}
                                    </Select>
                                </Space>
                                <Space>
                                    <Button
                                        icon={<PlusOutlined />}
                                        onClick={() => handleAddService(spec.id)}
                                        style={{ borderRadius: '8px', fontWeight: 600 }}
                                    >
                                        Add Service
                                    </Button>
                                    <Popconfirm
                                        title="Remove this speciality?"
                                        description="All associated services will also be removed from your profile."
                                        onConfirm={() => handleRemoveSpeciality(spec.id)}
                                        okText="Yes"
                                        cancelText="No"
                                    >
                                        <Button danger icon={<DeleteOutlined />} style={{ borderRadius: '8px' }} />
                                    </Popconfirm>
                                </Space>
                            </div>

                            <div className="spec-body">
                                {spec.services.length > 0 ? (
                                    spec.services.map((srv) => (
                                        <div key={srv.id} className="service-row">
                                            <div className="row align-items-start g-3">
                                                <div className="col-md-4">
                                                    <Text strong style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>SERVICE NAME</Text>
                                                    <Select
                                                        value={srv.service ? String(srv.service) : undefined}
                                                        onChange={(v) => handleDataChange(spec.id, srv.id, 'service', v)}
                                                        style={{ width: '100%' }}
                                                        placeholder="Choose Service"
                                                        showSearch
                                                    >
                                                        {services
                                                            .filter(s => String(s.speciality_id) === String(spec.speciality))
                                                            .map(s => (
                                                                <Option key={s.id} value={String(s.id)}>{s.name.replace(/&amp;/g, '&')}</Option>
                                                            ))
                                                        }
                                                    </Select>
                                                </div>
                                                <div className="col-md-2">
                                                    <Text strong style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>PRICE (₹)</Text>
                                                    <InputNumber
                                                        value={srv.price}
                                                        onChange={(v) => handleDataChange(spec.id, srv.id, 'price', v)}
                                                        style={{ width: '100%' }}
                                                        prefix={<DollarCircleOutlined style={{ color: '#94a3b8' }} />}
                                                        placeholder="Fee"
                                                    />
                                                </div>
                                                <div className="col-md-5">
                                                    <Text strong style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '4px' }}>DESCRIPTION (OPTIONAL)</Text>
                                                    <Input
                                                        value={srv.description}
                                                        onChange={(e) => handleDataChange(spec.id, srv.id, 'description', e.target.value)}
                                                        placeholder="Brief description of the service..."
                                                    />
                                                </div>
                                                <div className="col-md-1 d-flex justify-content-end" style={{ paddingTop: '24px' }}>
                                                    <Tooltip title="Remove Service">
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => handleRemoveService(spec.id, srv.id)}
                                                        />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-5 text-center">
                                        <Empty description="No services added for this speciality yet." />
                                        <Button type="dashed" onClick={() => handleAddService(spec.id)} style={{ marginTop: '12px' }}>
                                            Add First Service
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="You haven't added any specialities yet."
                        className="premium-spec-card p-5"
                    >
                        <Button type="primary" onClick={handleAddSpeciality}>Start Adding Specialities</Button>
                    </Empty>
                )}
            </div>

            <div className="mt-5 p-4" style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                <Space>
                    <InfoCircleOutlined style={{ color: '#2563eb' }} />
                    <Text type="secondary">Changes will only be permanent after clicking the <strong>Update All</strong> button.</Text>
                </Space>
            </div>
        </div>
    );
};

export default SpecialitiesServices;
